/**
 * Hook to handle SSO callback login.
 *
 * API Endpoint: GET /v4/sso/callback?data=...&signature=...
 *
 * Validates the signed data from the Booking Engine, authenticates
 * the user via AuthProvider, and redirects to the appropriate dashboard.
 */

import { useMutation } from "@tanstack/react-query";
import { publicApi } from "../../../api/api";
import { AxiosError } from "axios";
import { useAuth } from "../../../auth/context/AuthProvider";
import { useNavigate } from "react-router";
import { getRedirectPathFromPermissions } from "../../../auth/utils/getRedirectPathFromPermissions";
import { User } from "../../../auth/types/User";
import {
    SsoCallbackResponseSchema,
    type SsoCallbackResponse,
} from "../response/SsoCallbackResponse";

export interface SsoCallbackParams {
    data: string;
    signature: string;
}

export const useSsoCallback = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    return useMutation<SsoCallbackResponse, AxiosError, SsoCallbackParams>({
        mutationFn: async (params) => {
            if (!params.data || !params.signature) {
                throw new Error("Missing required SSO callback parameters: data and signature");
            }

            const response = await publicApi.get<SsoCallbackResponse>("/v4/sso/callback", {
                params: {
                    data: params.data,
                    signature: params.signature,
                },
            });

            // Validate response with Zod
            const validationResult = SsoCallbackResponseSchema.safeParse(response.data);
            if (!validationResult.success) {
                console.error("SSO callback validation failed:", validationResult.error.errors);
                throw new Error("Invalid SSO callback response format");
            }

            return validationResult.data;
        },
        onSuccess: (response) => {
            // Map API response to internal User type (same as useLogin)
            const userDataForAuth: User = {
                id: response.data.id,
                name: response.data.name,
                email: response.data.email,
                role: response.data.role,
                permissions: response.data.permissions,
                properties: response.data.properties,
                active_property_id: response.data.active_property_id,
                active_property: response.data.active_property,
                company: response.data.company,
                is_full_sync: response.data.is_full_sync,
                phone: response.data.phone,
            };

            login(userDataForAuth, response.token);

            const permissions = response.data?.permissions || [];
            const redirectPath = getRedirectPathFromPermissions(permissions);

            navigate(redirectPath);
        },
        onError: (error) => {
            console.error("SSO callback login failed:", error);
        },
        retry: false,
    });
};
